"use client";

import { ArrowRight, Menu, X } from "lucide-react";
import Link from "next/link";
import { useRef, useState } from "react";
import {
  PublicBookingCta,
  focusBookingTarget,
  type PublicBookingAction,
} from "./public-booking-cta";

import Logo from "~/components/logo";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "~/components/ui/sheet";
import { buttonVariants } from "~/components/ui/button";
import { Avatar, AvatarImage } from "~/components/ui/avatar";
import { useHeaderAccount } from "~/components/patterns/use-header-account";
import {
  PUBLIC_NAVIGATION,
  type PublicNavDestination,
} from "~/components/patterns/public-navigation";
import { cn } from "~/lib/utils";

type PublicMobileMenuProps = {
  onNavigate: (destination: PublicNavDestination) => void;
  onBookingCtaClick?: () => void;
  bookingAction?: PublicBookingAction;
  resumeBooking?: boolean;
  resumeTherapist?: { name?: string; image?: string };
  showBookingCta?: boolean;
  /** Renders the trigger icon white for a transparent header floating over
   * a dark hero, instead of the usual light-surface foreground color. */
  light?: boolean;
};

export function PublicMobileMenu({
  onNavigate,
  onBookingCtaClick,
  bookingAction,
  resumeBooking = false,
  resumeTherapist,
  showBookingCta = true,
  light = false,
}: PublicMobileMenuProps) {
  const account = useHeaderAccount();
  const [isOpen, setIsOpen] = useState(false);
  const pendingFocusTarget = useRef<string | null>(null);

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild>
        <button
          type="button"
          className={cn(
            "focus-visible:ring-brand inline-flex h-11 w-11 items-center justify-center rounded-full transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-hidden lg:hidden",
            light
              ? "text-white hover:bg-white/10"
              : "text-foreground hover:bg-muted",
          )}
          aria-label="Otvori meni"
        >
          <Menu aria-hidden="true" className="h-5 w-5" />
        </button>
      </SheetTrigger>
      <SheetContent
        side="right"
        onCloseAutoFocus={(event) => {
          const targetId = pendingFocusTarget.current;
          pendingFocusTarget.current = null;
          if (!targetId) return;

          // Transfer focus only after the dialog releases its focus scope.
          event.preventDefault();
          focusBookingTarget(targetId);
        }}
        aria-describedby={undefined}
        showClose={false}
        className="border-brand-forest/15 bg-editorial-paper text-foreground flex h-dvh w-[min(25rem,calc(100vw-1rem))] flex-col gap-0 p-0"
      >
        <SheetHeader className="border-brand-forest/15 flex h-20 flex-row items-center justify-between space-y-0 border-b px-6 text-left">
          <SheetTitle asChild>
            <Link
              href="/"
              aria-label="Rešimo Probleme, početna stranica"
              onClick={() => setIsOpen(false)}
            >
              <Logo surface="light" />
            </Link>
          </SheetTitle>
          <SheetClose asChild>
            <button
              type="button"
              className="border-brand-forest/15 text-foreground/70 hover:bg-brand-forest/5 hover:text-foreground focus-visible:ring-brand focus-visible:ring-offset-editorial-paper flex h-10 w-10 items-center justify-center rounded-full border transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-hidden"
              aria-label="Zatvori meni"
            >
              <X aria-hidden="true" className="h-5 w-5" />
            </button>
          </SheetClose>
        </SheetHeader>

        <nav
          className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-6 py-7"
          aria-label="Mobilna navigacija"
        >
          {account.isAuthenticated && (
            <SheetClose asChild>
              <Link
                href="/app/dashboard"
                className="border-brand-forest/15 text-foreground hover:text-brand focus-visible:ring-brand mb-7 flex min-h-14 items-center justify-between gap-4 border-b py-3 text-base font-semibold transition-colors focus-visible:ring-2 focus-visible:outline-hidden focus-visible:ring-inset"
              >
                {account.dashboardLabel}
                <ArrowRight
                  aria-hidden="true"
                  className="text-brand h-4 w-4 shrink-0"
                />
              </Link>
            </SheetClose>
          )}

          <MenuGroup label="Pronađi podršku">
            {PUBLIC_NAVIGATION.mobile.support.map((link) => (
              <MenuLink key={link.href} {...link} onNavigate={onNavigate} />
            ))}
          </MenuGroup>

          <div className="border-brand-forest/15 bg-brand-forest text-editorial-paper-contrast mt-7 rounded-xl border px-5 py-5">
            <p className="text-sm font-bold">Nisi siguran ko ti odgovara?</p>
            <p className="text-editorial-paper-contrast/75 mt-1 text-sm leading-relaxed">
              Odgovori na nekoliko pitanja i suzi izbor terapeuta.
            </p>
            <SheetClose asChild>
              <Link
                href={PUBLIC_NAVIGATION.mobile.questionnaire.href}
                onClick={() =>
                  onNavigate(PUBLIC_NAVIGATION.mobile.questionnaire.href)
                }
                className="text-editorial-paper-contrast decoration-editorial-paper-contrast/40 hover:decoration-editorial-paper-contrast mt-4 inline-flex items-center gap-2 text-sm font-semibold underline underline-offset-4"
              >
                {PUBLIC_NAVIGATION.mobile.questionnaire.label}
                <ArrowRight aria-hidden="true" className="h-4 w-4" />
              </Link>
            </SheetClose>
          </div>

          <MenuGroup className="mt-9" label="Saznaj više">
            {PUBLIC_NAVIGATION.mobile.resources.map((link) => (
              <MenuLink key={link.href} {...link} onNavigate={onNavigate} />
            ))}
          </MenuGroup>

          <div className="mt-9 pt-2">
            {account.isAuthenticated ? (
              <SheetClose asChild>
                <Link
                  href="/api/auth/signout?callbackUrl=/"
                  className="text-foreground hover:text-destructive focus-visible:ring-brand focus-visible:ring-offset-editorial-paper flex min-h-14 items-center text-sm font-medium transition-colors focus-visible:ring-2 focus-visible:ring-offset-4 focus-visible:outline-hidden"
                >
                  Odjavi se
                </Link>
              </SheetClose>
            ) : (
              <SheetClose asChild>
                <Link
                  href="/app/login"
                  className="text-foreground/70 hover:text-brand focus-visible:ring-brand focus-visible:ring-offset-editorial-paper flex min-h-14 items-center text-sm font-semibold transition-colors focus-visible:ring-2 focus-visible:ring-offset-4 focus-visible:outline-hidden"
                >
                  Prijava
                </Link>
              </SheetClose>
            )}
          </div>
        </nav>

        {showBookingCta && (
          <div className="border-brand-forest/15 bg-editorial-paper border-t px-6 pt-4 pb-[max(1.25rem,env(safe-area-inset-bottom))]">
            <PublicBookingCta
              action={bookingAction}
              onClick={() => {
                onBookingCtaClick?.();
                setIsOpen(false);
              }}
              onFocusRequest={(targetId) => {
                pendingFocusTarget.current = targetId;
              }}
              className={buttonVariants({
                variant: "cta",
                size: "lg",
                className: cn(
                  "h-auto min-h-12 w-full gap-3 px-5 py-3 whitespace-normal",
                  resumeBooking && resumeTherapist?.name && "min-h-16",
                ),
              })}
            >
              {resumeBooking && resumeTherapist?.image && (
                <Avatar className="h-8 w-8" aria-hidden="true">
                  <AvatarImage src={resumeTherapist.image} alt="" />
                </Avatar>
              )}
              <span className="min-w-0 flex-1 text-left">
                <span className="block text-base font-semibold">
                  {resumeBooking
                    ? "Nastavi zakazivanje"
                    : "Zakaži prvi razgovor"}
                </span>
                {resumeBooking && resumeTherapist?.name && (
                  <span className="block text-sm font-normal break-words text-white/85">
                    {resumeTherapist.name}
                  </span>
                )}
              </span>
              <ArrowRight aria-hidden="true" className="h-4 w-4 shrink-0" />
            </PublicBookingCta>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

function MenuGroup({
  label,
  className,
  children,
}: {
  label: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <section className={className} aria-label={label}>
      <p className="text-brand text-xs font-bold tracking-[0.16em] uppercase">
        {label}
      </p>
      <ul className="divide-brand-forest/15 border-brand-forest/15 mt-3 divide-y border-y">
        {children}
      </ul>
    </section>
  );
}

function MenuLink({
  label,
  href,
  onNavigate,
}: {
  label: string;
  href: PublicNavDestination;
  onNavigate: (destination: PublicNavDestination) => void;
}) {
  return (
    <li>
      <SheetClose asChild>
        <Link
          href={href}
          onClick={() => onNavigate(href)}
          className="text-foreground hover:text-brand focus-visible:ring-brand flex min-h-14 items-center justify-between gap-4 py-3 text-base font-semibold transition-colors focus-visible:ring-2 focus-visible:outline-hidden focus-visible:ring-inset"
        >
          {label}
          <ArrowRight
            aria-hidden="true"
            className="text-brand h-4 w-4 shrink-0"
          />
        </Link>
      </SheetClose>
    </li>
  );
}
